import React from 'react';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import { AmStatusType, DATE_ONLY_FORMAT, DISPLAY_EMPTY, SERVER_TIMEZONE } from './Constants';
import { LocaleObject } from '../contexts/LocalizationContext';

export const convertEmptyStringToDashes = (
    value: string | null | number,
    converter?: (x: number | string) => string,
) => {
    if (value === undefined || value === null || value === '') {
        return DISPLAY_EMPTY;
    }
    if (converter) {
        return converter(value);
    }
    return value;
};

export const convertEmptyOrZeroToDashes = (
    value: undefined | null | number,
    converter?: (x: number) => string,
) => {
    if (!value) {
        return DISPLAY_EMPTY;
    }
    if (converter) {
        return converter(value);
    }
    return value;
};

export const formatAmDisplayString = (amNumber: number | string, supNumber: number | string) =>
    `${amNumber}-${supNumber}`;

export const formatNumber = (x: number | string) =>
    new Intl.NumberFormat('en-US').format(Number(x));

export const formatDecimalNumber = (x: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'decimal',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    }).format(Number(x));

export const formatCurrency = (x: number | string, decimals?: number) => {
    // currencySign requires newer typescript library
    const options = {
        style: 'currency',
        currency: 'USD',
        currencySign: 'accounting',
        maximumFractionDigits: decimals !== undefined ? decimals : 2,
        minimumFractionDigits: decimals !== undefined ? decimals : 2,
    } as Intl.NumberFormatOptions;
    return new Intl.NumberFormat('en-US', options).format(Number(x));
};

export const formatNameTitle = (name: string, title: string | null) =>
    `${name}${title ? ` (${title})` : ''}`;

type Formatted = number | string | JSX.Element;
type FormatObject = { [key: string]: Formatted };

export const formatLocalizedString = (
    localizedStrings: LocaleObject,
    key: string,
    ...values: Array<Formatted | FormatObject>
) => {
    const formattedString = localizedStrings.formatString(key, ...values);
    if (typeof formattedString === 'string') {
        return formattedString;
    }
    return formattedString.join('');
};

export const noOptionsHandler = (input: { inputValue: string }) => {
    if (!input) {
        return null;
    }
    if (input.inputValue.length === 0) return 'Type to load options';
    return 'No options found';
};

export const sendToast = (content: React.ReactNode) => {
    if (toast) {
        toast.success(content, { hideProgressBar: true });
    }
};

const getStatusBadgeStyle = (status: string) => {
    let statusBadgeStyle = '';
    switch (status) {
        case AmStatusType.Approved:
            statusBadgeStyle = 'badge-success';
            break;
        case AmStatusType.Pending:
            statusBadgeStyle = 'badge-info';
            break;
        case AmStatusType.Cancelled:
        case AmStatusType.Closed:
        case AmStatusType.Draft:
        case AmStatusType.Hold:
        case AmStatusType.Inactive:
        case AmStatusType.Rejected:
        case AmStatusType.Voided:
            statusBadgeStyle = 'badge-secondary';
            break;
        default:
            statusBadgeStyle = 'badge-dark';
    }

    return statusBadgeStyle;
};

export const getStatusBadge = (status: string, forList: boolean = false) => {
    if (forList) {
        return (
            <>
                <span className="mobile-hide">{status}</span>
                <div className="desktop-hide text-right">
                    <span className={`badge ${getStatusBadgeStyle(status)}`}>{status}</span>
                </div>
            </>
        );
    }
    return <span className={`badge ${getStatusBadgeStyle(status)}`}>{status}</span>;
};

export const getDueInBadgeStyle = (dueIn: number) => {
    let badgeStyle = 'badge-danger';
    if (dueIn > 5) {
        badgeStyle = 'badge-success';
    } else if (dueIn > 3) {
        badgeStyle = 'badge-warning';
    }
    return badgeStyle;
};

export const formatDate = (date: string | null | undefined, format: string = DATE_ONLY_FORMAT) =>
    date ? convertISO8601ToTimezone(date, SERVER_TIMEZONE, format) : convertEmptyStringToDashes('');

export const renderDateCell = (cellProps: { value: string }) => (
    <span>{formatDate(cellProps.value)}</span>
);

export const truncateString = (str: string, length: number = 20) =>
    str.length > length ? `${str.substr(0, length - 3)}...` : str;

export const formatExportText = (str: string | null | undefined) =>
    str ? `"${str.replace(/"/g, '""')}"` : '';

export const formatBooleanAsYesNo = (
    boolValue: boolean | null | undefined,
    defaultValue: boolean = false,
) =>
    boolValue || ((boolValue === null || boolValue === undefined) && defaultValue) ? 'Yes' : 'No';

const DEFAULT_FORMAT = 'MM/DD/YYYY hh:mm A z';

export const convertISO8601ToTimezone = (date: string, timeZone: string, format = DEFAULT_FORMAT) =>
    moment.tz(date, timeZone).format(format);
